import React, { useEffect, useRef, useState } from "react";
import { Bell, Ban } from "lucide-react";
import api from "@/lib/api";

export default function NotificationBell() {
  const [data, setData] = useState({ unread: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      try { await api.post("/notifications/read-all"); setData((d) => ({ ...d, unread: 0 })); } catch { /* ignore */ }
    }
  };

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" aria-label="Notifications"
        className="relative flex items-center justify-center w-10 h-10 rounded-md border border-race-line text-race-sub hover:border-race-red hover:text-race-red transition-colors">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-race-red text-race-bg text-[10px] font-bold">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 top-12 z-50 w-80 max-h-[70vh] overflow-auto bg-race-surface border border-race-line rounded-md shadow-2xl animate-slide-up">
          <div className="px-4 py-3 border-b border-race-line sticky top-0 bg-race-surface">
            <span className="font-heading text-lg tracking-tight">NOTIFICATIONS</span>
          </div>
          {data.notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-race-sub text-sm" data-testid="notification-empty">
              You're all caught up.
            </div>
          ) : (
            <div className="divide-y divide-race-line">
              {data.notifications.map((n, i) => (
                <div key={i} data-testid="notification-item"
                  className={`px-4 py-3 ${n.read ? "" : "bg-race-red/5"}`}>
                  <div className="flex items-start gap-2.5">
                    <Ban size={15} className="text-race-red mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-race-txt font-medium">{n.title}</div>
                      <div className="text-xs text-race-sub mt-0.5">{n.body}</div>
                      {n.refund_credits > 0 && (
                        <div className="text-xs text-race-volt mt-1">+{n.refund_credits} credits refunded</div>
                      )}
                      <div className="text-[10px] text-race-muted mt-1">{fmt(n.created_at)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
